import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import * as React from 'react';
import { Button } from '~/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/lib/utils';

export interface SearchableSelectOption {
  id: string;
  name: string;
  jpName?: string | null;
  slug?: string;
}

interface SearchableSelectProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  onSearch: (query: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  emptyText?: string;
  isLoading?: boolean;
  allowClear?: boolean;
  className?: string;
}

export function SearchableSelect({
  value,
  onChange,
  onSearch,
  options,
  placeholder = 'Select...',
  emptyText = 'No results found.',
  isLoading = false,
  allowClear = true,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const selectedOption = options.find((opt) => opt.id === value);

  const handleSearch = React.useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchValue: string) => {
      setSearch(searchValue);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        onSearch(searchValue);
      }, 300);
    };
  }, [onSearch]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
        >
          {selectedOption ? (
            <span className='truncate'>
              {selectedOption.name}
              {selectedOption.jpName && (
                <span className='ml-2 text-xs text-muted-foreground'>({selectedOption.jpName})</span>
              )}
            </span>
          ) : (
            <span className='text-muted-foreground'>{placeholder}</span>
          )}
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[400px] p-0' align='start'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search ${placeholder.toLowerCase()}...`}
            value={search}
            onValueChange={handleSearch}
          />
          <CommandList>
            {isLoading ? (
              <div className='flex items-center justify-center py-6'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span className='ml-2 text-sm text-muted-foreground'>Loading...</span>
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {allowClear && value && (
                    <CommandItem
                      value='__clear__'
                      onSelect={() => {
                        onChange(null);
                        setOpen(false);
                        setSearch('');
                      }}
                      className='text-muted-foreground italic'
                    >
                      Clear selection
                    </CommandItem>
                  )}
                  {options.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={option.id}
                      onSelect={(currentValue) => {
                        onChange(currentValue === value ? null : currentValue);
                        setOpen(false);
                        setSearch('');
                      }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', value === option.id ? 'opacity-100' : 'opacity-0')} />
                      <span className='flex-1 truncate'>
                        {option.name}
                        {option.jpName && <span className='ml-2 text-xs text-muted-foreground'>({option.jpName})</span>}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
